import { apiClient } from './client'
import type {
  StructureUploadResponse,
  StructureSaveRequest,
  StructureSaveResponse,
  BedFundUploadResponse,
  BedFundSaveRequest,
  BedFundSaveResponse,
  BedFundViewResponse,
  BedFundPeriodsResponse,
  BedFundPeriodCheckResponse,
  BedFundUpdateBatchRequest,
  BedFundUpdateBatchResponse,
  FileBatch,
  FileBatchConfirmRequest,
  FileBatchConfirmResponse,
  FileBatchPreparePreviewRequest,
  FileIntakeExecutionStatus,
  FileBatchListResponse,
} from '@/types/etl'

// ============================================
// Structure (Структура подразделений)
// ============================================
export const uploadStructure = async (file: File): Promise<StructureUploadResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await apiClient.post('/etl/upload/structure?only_otdelenie=true', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return response.data
}

export const saveStructure = async (request: StructureSaveRequest): Promise<StructureSaveResponse> => {
  const response = await apiClient.post('/etl/structure/save', request)
  return response.data
}

// ============================================
// Bed Fund (Коечный фонд)
// ============================================
export const uploadBedFund = async (file: File): Promise<BedFundUploadResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await apiClient.post('/refs/bed-fund/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return response.data
}

export const saveBedFund = async (request: BedFundSaveRequest): Promise<BedFundSaveResponse> => {
  const response = await apiClient.post('/refs/bed-fund/save', request)
  return response.data
}

export const getBedFundPeriods = async (): Promise<BedFundPeriodsResponse> => {
  const response = await apiClient.get('/refs/bed-fund/periods')
  return response.data
}

export const checkBedFundPeriod = async (period: string): Promise<BedFundPeriodCheckResponse> => {
  const response = await apiClient.get(`/refs/bed-fund/periods/${period}/check`)
  return response.data
}

export const getBedFund = async (params: {
  period: string
  page?: number
  page_size?: number
  search?: string
  fil_code?: number
  usl_ok_code?: string
}): Promise<BedFundViewResponse> => {
  const response = await apiClient.get('/refs/bed-fund/', { params })
  return response.data
}

export const deleteBedFundPeriod = async (period: string): Promise<{ success: boolean; deleted_count: number }> => {
  const response = await apiClient.delete(`/refs/bed-fund/periods/${period}`)
  return response.data
}

export const updateBedFundBatch = async (request: BedFundUpdateBatchRequest): Promise<BedFundUpdateBatchResponse> => {
  const response = await apiClient.patch('/refs/bed-fund/update-batch', request)
  return response.data
}

// ============================================
// File Intake (реестры / МЭК / evidence files)
// ============================================
export const listFileBatches = async (params?: {
  status?: string
  limit?: number
}): Promise<FileBatchListResponse> => {
  const response = await apiClient.get('/etl/file-batches', { params })
  return response.data
}

export const getFileBatch = async (batchId: string): Promise<FileBatch> => {
  const response = await apiClient.get(`/etl/file-batches/${batchId}`)
  return response.data
}

export const uploadFileBatch = async (params: {
  files: File[]
  name?: string
  expectedOrgId: string
}): Promise<FileBatch> => {
  const formData = new FormData()
  if (params.name) {
    formData.append('name', params.name)
  }
  params.files.forEach((file) => formData.append('files', file))

  const response = await apiClient.post('/etl/file-batches/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'X-Medcifra-Expected-Org-Id': params.expectedOrgId,
    },
  })
  return response.data
}

export const validateFileBatch = async (
  batchId: string,
  expectedOrgId: string
): Promise<FileBatch> => {
  const response = await apiClient.post(
    `/etl/file-batches/${batchId}/validate`,
    undefined,
    { headers: { 'X-Medcifra-Expected-Org-Id': expectedOrgId } }
  )
  return response.data
}

export const prepareFileBatchPreview = async (
  batchId: string,
  request: FileBatchPreparePreviewRequest,
  expectedOrgId: string
): Promise<FileBatch> => {
  const response = await apiClient.post(
    `/etl/file-batches/${batchId}/prepare-preview`,
    request,
    { headers: { 'X-Medcifra-Expected-Org-Id': expectedOrgId } }
  )
  return response.data
}

export const confirmFileBatch = async (
  batchId: string,
  request: FileBatchConfirmRequest,
  expectedOrgId: string
): Promise<FileBatchConfirmResponse> => {
  const response = await apiClient.post(`/etl/file-batches/${batchId}/confirm`, request, {
    headers: { 'X-Medcifra-Expected-Org-Id': expectedOrgId },
  })
  return response.data
}

export const getFileBatchExecution = async (
  batchId: string,
  signal?: AbortSignal
): Promise<FileIntakeExecutionStatus> => {
  const response = await apiClient.get(`/etl/file-batches/${batchId}/execution`, {
    signal,
  })
  return response.data
}
